import { useMutation } from "@apollo/client";
import { Button, Checkbox, FormLabel } from "@mui/material";
import React, { useState } from "react";
import Resizer from "react-image-file-resizer";
import { ADD_IMAGE } from "../graphql";
import { s3Put } from "../s3";
import { ButtonContainer } from "../../../components/ButtonContainer";
import { PageContainer } from "../../../components/PageContainer";
import { EditImageContainer } from "./Select";
import { Note } from "../../../components/Note";
import { toast } from "react-toastify";
import { useIsSuperAdmin } from "../../../App";
import { getImageUrlFromResult, getNonProxiedCloudflareUrl, getUpdatedImageUrl } from "../../post/edit/helpers";

interface IUploadProps {
  file: string;
  fileName: string;
  returnAction: (imageUrl: string) => void;
}
export const callCompressor = async (
  url: string | undefined,
  extraCompression: boolean
): Promise<string> => {
  if (!extraCompression || !url) return url as string;

  const isCloudflare =
    url.includes("cdn.meliora.ltd") || url.includes("cloudflarestorage.com");

  const updatedUrl = isCloudflare ? getNonProxiedCloudflareUrl(url): url;

  const compressorUrl = isCloudflare
    ? "https://2ae42x3ml26dsggy4ughykbtva0hnhxh.lambda-url.eu-west-1.on.aws/"
    : "https://2cwxqg1gzb.execute-api.eu-west-1.amazonaws.com/default/imageCompressor";

  try {
    const response = await fetch(compressorUrl, {
      method: "POST",
      body: JSON.stringify({ imageUrl: updatedUrl }),
    });
    const data = await response.json();
    toast("Compression successful", { type: "success" });

    return getUpdatedImageUrl(data.url) as string
  } catch (ex) {
    console.log("Compressor errored!", ex);
    toast("Stage 2 compression step was not successful", { type: "warning" });
    return url;
  }
};

const Upload = ({ file, fileName, returnAction }: IUploadProps) => {
  const [image, setImage] = useState<string>(file);
  const [resizedFile, setResizedFile] = useState<string>();
  const [getImageUrl, { data, error }] = useMutation(ADD_IMAGE, {});
  const [_, actualExtension] = (fileName as string).split(".");
  const [extraCompression, setExtraCompression] = useState<boolean>(false);
  const isSuperAdmin = useIsSuperAdmin();

  const resizeFile = (file: any) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        1200, // maxWidth
        1200, // maxHeight
        actualExtension.toUpperCase() ?? "JPEG", // compress format
        0.5, // quality.
        0,
        (uri) => {
          resolve(uri);
        },
        "base64"
        // 1500, // minWidth?
        // 1500 // minHeight
      );
    });

  const handleClick = async () => {
    try {
      const fileBlob = await fetch(file).then((r) => r.blob());
      const image = (await resizeFile(fileBlob)) as string;
      setResizedFile(image);
      toast("Resize complete");
    } catch (err) {
      console.log(err);
    }
  };
  const skipStep = async () => {
    try {
      const fileBlob = await fetch(file).then((r) => r.blob());
      setResizedFile(URL.createObjectURL(fileBlob));
      toast("Compression skipped");
    } catch (err) {
      console.log(err);
    }
  };

  const handleSave = async () => {
    const res = await getImageUrl({ variables: { image: fileName } });

    if (!res.data?.addImage || !resizedFile) return;

    const fileBlob = await fetch(resizedFile).then((r) => r.blob());

    await s3Put(res.data?.addImage, fileBlob);

    const url = getImageUrlFromResult(res.data.addImage);

    const compressedUrl = await callCompressor(url, extraCompression);

    returnAction(compressedUrl);
  };

  return (
    <PageContainer title="Resize & Upload">
      <EditImageContainer>
        <ButtonContainer>
          <Button title="Reize" onClick={handleClick}>
            Resize
          </Button>
          {isSuperAdmin && (
            <Button title="Reize" onClick={skipStep}>
              Skip step
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!resizedFile}
          >
            Upload
          </Button>
          {isSuperAdmin && (
            <>
              <FormLabel>Enable extra compression</FormLabel>
              <Checkbox
                id="outlined-basic"
                onChange={() => setExtraCompression(!extraCompression)}
                defaultChecked={extraCompression}
                value={extraCompression}
              />
            </>
          )}
        </ButtonContainer>
      </EditImageContainer>
      <Note
        message={`Info: This step will greatly reduce the file size of the image \n a preview of the prepared image can be found below`}
        style={{ left: 0 }}
      />

      <h4>Before</h4>
      <img src={image} alt="" width={"75%"} />

      <h4>After</h4>
      <img src={resizedFile} alt="" width={"75%"} />
    </PageContainer>
  );
};

export default Upload;
